﻿import { Coordinate } from 'ol/coordinate';
import { getSize, isEmpty } from "ol/extent";
import { Options } from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import LinearRing from "ol/geom/LinearRing";
import LineString from "ol/geom/LineString";
import MultiLineString from "ol/geom/MultiLineString";
import MultiPoint from "ol/geom/MultiPoint";
import MultiPolygon from "ol/geom/MultiPolygon";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import Map from "ol/Map";
import { ProjectionLike } from "ol/proj";

import { Projections } from "../models/Projections";
import { DEGREES_360 } from "../utilities/DateLineUtilities";
import GeodesicCircle from "./custom/GeodesicCircle";
import * as ext from "./extensions";
import { GeometryExtensionUtilities } from "./extensions/BaseGeometryExtension";
import GeometryConverter from "./GeometryConverter";
import { CoordinatesResult } from "./models/CoordinateResult";
import { Coordinates, isCoordinate, isCoordinatesArray, isFlatCoordinateArray } from "./models/Coordinates";

/**
 * Composition class for openlayers Geometry class.
 * Consolidates extended classed of Geometry and provided added functionality.
 */
export class GeometryExtensions {

    private extension: ext.BaseGeometryExtension<Geometry> | undefined;

    constructor()
    constructor(geometry: Geometry)
    constructor(geojson: GeoJSON.GeoJSON, options?: Options)
    constructor(data?: Geometry | GeoJSON.GeoJSON, options?: Options) {

        if (data) {
            if (data instanceof Geometry)
                this.setGeometry(data);
            else
                this.setGeojson(data, options);
        }
    }

    public getGeometry() {
        return this.extension?.geometry;
    }

    public getSuperType() {
        return this.extension?.supertype;
    }

    public setGeometry(geometry: Geometry | undefined): void {

        if (!geometry) {
            this.extension = undefined;
        }
        else if (geometry !== this.getGeometry()) {
            if (geometry instanceof Point)
                this.extension = new ext.PointExtension(geometry);
            else if (geometry instanceof MultiPoint)
                this.extension = new ext.MultiPointExtension(geometry);
            else if (geometry instanceof LineString)
                this.extension = new ext.LineStringExtension(geometry);
            else if (geometry instanceof MultiLineString)
                this.extension = new ext.MultiLineStringExtension(geometry);
            else if (geometry instanceof LinearRing)
                this.extension = new ext.LinearRingExtension(geometry);
            else if (geometry instanceof Polygon)
                this.extension = new ext.PolygonExtension(geometry);
            else if (geometry instanceof MultiPolygon)
                this.extension = new ext.MultiPolygonExtension(geometry);
            else {
                const geoCircle = GeodesicCircle.isGeodesicCircle(geometry);
                if (geoCircle)
                    this.extension = new ext.GeodesicCircleExtension(geoCircle)
                else {
                    console.log("Failed to set extension for", geometry)
                    this.extension = undefined;
                }
            }
        }
    }

    public getGeoJson(options?: Options) {
        const geometry = this.getGeometry();
        if (geometry)
            return GeometryConverter.getGeojsonGeometry(geometry, options);
        return undefined
    }

    public setGeojson(geojson: GeoJSON.GeoJSON, options?: Options) {
        const geometry = GeometryConverter.getOpenlayersGeometry(geojson, options)
        if (geometry)
            this.setGeometry(geometry);
    }

    public getCoordinates() {
        return this.extension?.getCoordinates();
    }

    public setCoordinates(data: Coordinates) {
        try {
            this.extension?.setCoordinates(data);
            return !!this.extension;
        }
        catch {
            console.error("Failed to set coordinates")
            return false;
        }
    }

    public getRadius(projection: ProjectionLike = Projections.WebMercator) {
        return this.extension?.getRadius(projection);
    }

    public setRadius(radius: number, projection: ProjectionLike = Projections.WebMercator) {
        this.extension?.setRadius(radius, projection);
    }

    public findCoordinate(coordinate: Coordinate): number[] | undefined {

        const find = (data: Coordinates, path: number[]) => {
            let point = isCoordinate(data);
            if (point) {
                if (GeometryExtensionUtilities.samePoints(point, coordinate))
                    return path;
            }
            else {
                const list = isCoordinatesArray(data);
                if (list) {
                    for (var i = 0; i < list.length; i++) {
                        const p = [...path, i]
                        const result = find(list[0], p);
                        if (result)
                            return p;
                    }
                }
            }

            return undefined;
        }

        return find(this.getCoordinates() ?? [], []);
    }

    public findClosestCoordinates(coordinate: Coordinate, map: Map, pointTolerance: number = 0, excludePaths?: number[][]): CoordinatesResult | undefined {

        const supertype = this.extension?.supertype
        const interpolate = supertype == "LineString" || supertype === "Polygon";
        const data = this.getCoordinates() || [];

        // For polygons, if path refers to first vertex of a ring include last vertex and vice versa (since first and last are the same...)
        if (excludePaths && this.extension?.supertype === "Polygon") {
            const toBeAdded: number[][] = []
            excludePaths.forEach(path => {
                if (path.length) {
                    const arrayPath = path.slice(0, path.length - 1);
                    const array = isFlatCoordinateArray(this.walkPath(arrayPath));
                    if (array) {
                        const index = path.slice(-1)[0];
                        const first = 0;
                        const last = array.length - 1;

                        if (first < last) {
                            if (index === first)
                                toBeAdded.push([...arrayPath, last])
                            else if (index === last)
                                toBeAdded.push([...arrayPath, first])
                        }
                    }
                }
            })

            toBeAdded.forEach(add => {
                if (excludePaths.every(p => GeometryExtensionUtilities.samePoints(p, add) === false)) {
                    excludePaths.push(add);
                }
            })
        }

        const closest = GeometryExtensionUtilities.findClosestPoint(coordinate, data, interpolate, map, excludePaths);

        if (closest) {
            const { exact, interpolated } = closest;

            let coordinates: Coordinates = exact.point;
            let path = exact.path;
            

            if (pointTolerance > 0 && exact.pixelDistance > pointTolerance) {
                // if pixel distance too far set path 1 level higher (eg, point to line)
                const newPath = path.slice(0, path.length - 1);
                const walkResult = this.walkPath(newPath, data, true);
                if (walkResult) {
                    coordinates = walkResult;
                    path = newPath;
                }
            }

            return {
                coordinates,
                path,
                pixelDistance: interpolated?.pixelDistance ?? exact.pixelDistance,
                relativeDistance: exact.relativeDistance,
                interpolatedPoint: interpolated?.point
            }
        }

        return undefined;
    }

    public walkPath(path: number[], coordinates?: Coordinates, extactPath?: boolean): Coordinates | undefined {
        coordinates = coordinates || this.getCoordinates();
        if (coordinates)
            return GeometryExtensionUtilities.walkPath(path, coordinates, extactPath);
        return undefined
    }

    public replaceGeometry(geometry: Geometry | GeoJSON.GeoJSON, path: number[]) {
        if (this.extension) {
            if (!(geometry instanceof Geometry)) {
                const geometry2 = GeometryConverter.getOpenlayersGeometry(geometry);
                if (geometry2)
                    geometry = geometry2;
                else
                    return undefined;
            }

            path = path.slice();
            return this.extension.replaceGeometry(geometry, path);
        }

        return undefined;
    }

    public insertGeometry(geometryOrGeojson: Geometry | GeoJSON.GeoJSON, path: number[]): number[] | undefined {
        if (this.extension) {
            var geometry: Geometry;

            if (geometryOrGeojson instanceof Geometry) {
                geometry = geometryOrGeojson;
            }
            else {
                const geometry2 = GeometryConverter.getOpenlayersGeometry(geometryOrGeojson);
                if (geometry2)
                    geometry = geometry2;
                else
                    return undefined;
            }                

            var { geometry, path } = this.extension.adjustGeometryToBeInserted(geometry, path);

            path = path.slice();
            const self = this.getCoordiateNestingLevel();
            const other = new GeometryExtensions(geometry).getCoordiateNestingLevel();

            // resetting path after converting to multigeometry
            if (other >= self && this.convertToMultiGeometry())
                path = [Math.min(path[0] ?? 0, 1)];

            return this.extension.insertGeometry(geometry, path);
        }

        return undefined;
    }

    public removeGeometry(path: number[]) {
        path = path.slice();
        if (this.extension && this.extension.canRemove(path)) {
            const result = this.extension.removeGeometry(path);
            if (result) {
                let coords = isCoordinatesArray(this.getCoordinates());
                if (coords?.length === 1)
                    this.convertToSingleGeometry();

                return result;
            }
        }
        return undefined;
    }

    public canRemoveGeometry(path: number[]) {
        return this.extension?.canRemove(path) ?? false;
    }

    public canEditGeometry(path: number[]) {
        return this.extension?.canRemove(path) ?? false;
    }

    public convertToMultiGeometry() {
        if (this.extension) {
            const multi = this.extension.getMultiGeometry();
            if (multi) {
                this.setGeometry(multi);
                return true;
            }
        }
        return false;
    }

    public convertToSingleGeometry() {
        if (this.extension) {
            const single = this.extension.getSingleGeometry();
            if (single) {
                this.setGeometry(single);
                return true;
            }
        }
        return false;
    }


    public getCoordiateNestingLevel() {
        const get = (level: number, data?: Coordinates): number => {
            let list = isCoordinatesArray(data);
            if (list) {
                level++;
                return get(level, list[0]);
            }

            return level;
        }

        return get(0, this.getCoordinates());
    }

    public getFlattenCoordinates() {
        return this.extension?.getFlattenCoordinates() ?? [];
    }

    /**
     * Groups geometry sections which have been split at datelines.
     * This will result in longitudes that exceed +/-180 degrees.
     * Returns true if sections have been groups.
     */
    public groupSectionsAtDateLine() {
        if (this.extension) {
            const ext = this.getGeometry()?.getExtent();
            if (ext && !isEmpty(ext)) {
                const [width,] = getSize(ext);
                if (width >= (0.95 * DEGREES_360) && width <= DEGREES_360) {
                    this.extension.groupSectionsAtDateLine();
                    return true;
                }
            }
        }

        return false;
    }

    public mergeSnappedPoint() {
        return this.extension?.mergeSnappedPoints() === true;        
    }
}